import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
  DialogTrigger,
} from "@/taskpane/components/ui/dialog";
import { Button } from "@/taskpane/components/ui/button";
import { Textarea } from "@/taskpane/components/ui/textarea";
import { useTileEdit } from "@/hooks/useTileEdit";
import { ReviewTileItem } from "@/types/tile.types";
import { useDocument } from "@/hooks/useDocument";


interface EditTileDialogProps {
  tile: ReviewTileItem;
  queryTerm: string;
  children: React.ReactNode;
}

export function EditTileDialog({ tile, queryTerm, children }: EditTileDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [editedText, setEditedText] = useState("");
  const [editableKey, setEditableKey] = useState<string>("");
  const { data: document } = useDocument();

  const { mutate: editTile, isPending: isLoading } = useTileEdit();

  useEffect(() => {
    if (tile && tile.originalData) {
      const extraData = tile.originalData;
      if (extraData.suggestedclause) {
        setEditableKey("suggestedclause");
        setEditedText(extraData.suggestedclause);
      } else if (extraData.suggestion) {
        setEditableKey("suggestion");
        setEditedText(extraData.suggestion);
      } else if (extraData.suggestions) {
        setEditableKey("suggestions");
        setEditedText(
          Array.isArray(extraData.suggestions)
            ? extraData.suggestions.join("\n")
            : extraData.suggestions
        );
      }
    }
  }, [tile]);

  const handleSave = () => {
    if (!document || !editableKey) return;

    const payload = {
      document_id: [document._id],
      query_term: queryTerm,
      tile_id: tile.originalData.tile_id,
      tile: {
        ...tile,
        [editableKey]: editedText,
      },
    };
    editTile(payload, {
      onSuccess: () => {
        setIsOpen(false);
      },
      onError: (error) => {
        console.error("Error updating tile:", error);
      },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Tile</DialogTitle>
        </DialogHeader>
        <Textarea
          value={editedText}
          onChange={(e) => setEditedText(e.target.value)}
          rows={10}
          disabled={isLoading}
        />
        <DialogFooter className="flex-row justify-end gap-2">
          <DialogClose asChild>
            <Button variant="outline" disabled={isLoading}>
              Cancel
            </Button>
          </DialogClose>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
